import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { contentfulClient } from '@/contentful/api/client.contentful';
import { IMainNav } from '@/contentful/types';

import { useLanguage } from '@hooks/useTranslate';
import { LanguageKey } from '@models/Language';
import { apartmentStore } from '@stores/apartmentStore';
import { useAuth } from '@stores/authStore';
import { contractStore, useContract } from '@stores/contractStore';
import { userStore } from '@stores/userStore';

import { parseMainNavTabs } from './MainNavigation.utils';

const createMainNavQueryKey = (language: LanguageKey) =>
  ['main-navigation', language] satisfies QueryKey;

const fetchMainNavigation = async (locale: LanguageKey) => {
  const queryResult = await contentfulClient.getParsedEntries<IMainNav>({
    content_type: 'mainNav',
    include: 10,
    locale,
    limit: 1,
  });

  if (!queryResult.length) return null;

  // There should always be just 1 main navigation entry
  return queryResult[0];
};

export const useContentfulMainNavigation = () => {
  const { isAuthenticated } = useAuth();
  const language = useLanguage();
  const { pathname } = useLocation();
  const { contract } = useContract();

  const { data, isFetched, isFetching, ...queryProps } = useQuery(
    createMainNavQueryKey(language),
    () => fetchMainNavigation(language)
  );

  const mainNavTabs = useMemo(() => {
    if (!data) return undefined;

    return parseMainNavTabs(data, { contractStore, apartmentStore, userStore });
    // main tabs include conditional entries and should be therefore updated on contract change
  }, [data, contract]);

  return {
    ...queryProps,
    tabs: isAuthenticated
      ? mainNavTabs?.map((tab) => ({
          ...tab,
          isActive:
            'links' in tab
              ? // show tab as active if we're on some of the tab link group's page
                tab.links.some(({ to }) => to === pathname)
              : // ... or if were on a page that's directly linked
                tab.to === pathname,
        }))
      : [],
    isFetching: isFetching || (!!data && !mainNavTabs),
    isFetched,
  };
};
